// src/hooks/useAuth.js — BULLETPROOF VERSION
// Fixed: loading never gets stuck, timeout fallback, error handling
import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true

    // Safety timeout - NEVER stay in loading state more than 8 seconds
    // (padded to comfortably fit the getSessionWithRetry backoff below)
    const timeout = setTimeout(() => {
      if (mounted) setLoading(false)
    }, 8000)

    async function init() {
      try {
        const session = await getSessionWithRetry()
        if (!mounted) return

        setSession(session)

        if (session?.user?.id) {
          await loadProfile(session.user.id, mounted)
        } else {
          setLoading(false)
        }
      } catch (err) {
        console.error('Auth init error:', err)
        if (mounted) setLoading(false)
      } finally {
        clearTimeout(timeout)
      }
    }

    // A backgrounded tab can be fully reloaded by the browser (e.g. Chrome
    // discarding it to save memory) rather than just going invisible. On
    // that fresh reload, the network stack can be momentarily not ready,
    // causing this very first getSession() call to fail or return nothing
    // even though a valid token is sitting right there in storage. If a
    // stored token exists, retry a couple of times before accepting "no
    // session" — a real logged-out visitor (no stored token) still resolves
    // immediately with no added delay.
    async function getSessionWithRetry(retries = 2, delayMs = 700) {
      const hasStoredToken = () =>
        Object.keys(localStorage).some(k => k.includes('-auth-token'))

      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          const { data, error } = await supabase.auth.getSession()
          if (error) throw error
          if (data?.session) return data.session
        } catch (err) {
          if (attempt === retries) return null
        }
        if (!hasStoredToken() || attempt === retries) return null
        await new Promise(r => setTimeout(r, delayMs))
      }
      return null
    }

    init()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!mounted) return

        if (!newSession) {
          // A backgrounded tab can have its network connections closed by the
          // browser. The instant the tab wakes up, Supabase's own token-refresh
          // request can hit one of those dead connections and fail with a
          // network error — that's a connectivity blip, not a real sign-out.
          // Only clear the user if this check *confirms* there's no session;
          // if the check itself fails on a network error, leave the existing
          // session alone and let it settle instead of logging them out.
          try {
            const { data, error } = await supabase.auth.getSession()
            if (!mounted) return
            if (error) throw error

            if (data?.session) {
              setSession(data.session)
              await loadProfile(data.session.user.id, mounted)
              return
            }
            // Confirmed: no session exists. This is a real sign-out.
            setSession(null)
            setProfile(null)
            setLoading(false)
            return
          } catch (err) {
            // Network/connection error while checking — not a confirmed
            // sign-out. Leave current session state untouched.
            console.warn('Session check failed (likely a network blip on tab resume), keeping current session:', err)
            return
          }
        }

        setSession(newSession)
        await loadProfile(newSession.user.id, mounted)
      }
    )

    return () => {
      mounted = false
      clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, [])

  // Live-sync this user's own profile row (e.g. tier changed by admin)
  // so the dashboard reflects it immediately without a re-login.
  useEffect(() => {
    const userId = session?.user?.id
    if (!userId) return

    const channel = supabase
      .channel(`profile-live-${userId}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${userId}` },
        payload => {
          setProfile(prev => prev ? { ...prev, ...payload.new } : payload.new)
        })
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [session?.user?.id])

  async function loadProfile(userId, mounted = true) {
    try {
      // Timeout for profile fetch - don't hang if Supabase is slow
      const profilePromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile fetch timeout')), 4000)
      )

      const { data, error } = await Promise.race([profilePromise, timeoutPromise])
        .catch(err => ({ data: null, error: err }))

      if (!mounted) return

      if (data) {
        setProfile(data)
      } else {
        // A profile refetch can be slow or fail right as a backgrounded tab
        // regains focus and the network is still catching up (Supabase also
        // re-fires a token refresh at that moment, which triggers this same
        // function again). That's a transient blip, not proof the user has
        // no profile — never let it wipe out one we already successfully
        // loaded. Only stays null if we never had one to begin with.
        console.warn('Profile fetch failed or timed out — keeping existing profile if any:', error)
        setProfile(prev => prev ?? null)
      }
      setLoading(false)
    } catch (err) {
      console.error('Profile load error:', err)
      if (mounted) setLoading(false)
    }
  }

  async function refreshProfile() {
    if (session?.user?.id) {
      await loadProfile(session.user.id)
    }
  }

  return (
    <AuthContext.Provider value={{ session, profile, loading, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) return { session: null, profile: null, loading: false, refreshProfile: () => {} }
  return ctx
}
